import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import React, { useContext, useMemo, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { Location, LocationCreateDTO, Contact } from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { CircularProgress } from '_lib/components/CircularProgress/CircularProgress';
import { ControlHeader } from '_lib/components/ControlHeader/ControlHeader';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { ManualErrorBoundary, RecoverableErrorState } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { LocationForm } from '_lib/featureComponents/Location/LocationForm';
import { reportException } from '_lib/utils/sentryService';

import { Contacts } from '../Contacts/Contacts';

import { LocationList } from '../LocationList/LocationList';

import type { ApiSubmitHandler } from '_lib/types/hookForm.types';

export interface LocationsProps {
  companyId: string;
  providerCustomerId: string;
  locations: Location[];
  locationContactsMap: {
    [locationProviderId: string]: Contact[];
  };
  onLocationsUpdated: (locations: Location[]) => void;
  onLocationContactsUpdated: (locationProviderId: string, contacts: Contact[]) => void;
}

export const Locations = (props: LocationsProps): React.ReactElement => {
  const {
    companyId,
    providerCustomerId,
    locations,
    locationContactsMap,
    onLocationsUpdated,
    onLocationContactsUpdated
  } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(companyId), [api, companyId]);

  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location & { contacts: Contact[] }>();

  const [validationErrors, setValidationErrors] = useState<string[] | undefined>(undefined);
  const [recoverableError, setRecoverableError] = useState<RecoverableErrorState | null>(null);

  /**
   * Fetches contacts for uncached locations that have not been touched (edit clicked) yet, then updates
   * selectedLocation & locationContactsMap via onLocationContactsUpdated
   *
   * @param location
   * @returns whether or not we were able to lazily load and set the selected location
   */
  const lazyLoadSelectedLocation = async (location: Location): Promise<boolean> => {
    // Use the presence of the contacts array as a signal that contacts have been previously loaded. Skip
    // appending contacts if the contacts array exists, even if the array is empty. Do continue to load if
    // the append is forced.
    const cachedLocationContacts = locationContactsMap[location.providerId];
    if (cachedLocationContacts) {
      setSelectedLocation({ ...location, contacts: cachedLocationContacts });
      return true;
    }

    setIsLoading(true);

    let success = false;
    try {
      const contactsResponse = await serviceTitanApi.locations.contacts(location.providerId).unpagedList({});

      onLocationContactsUpdated(location.providerId, contactsResponse.results);
      setSelectedLocation({ ...location, contacts: contactsResponse.results });
      success = true;
    } catch (err) {
      setRecoverableError({ error: err, message: 'Failed to load location - Please Try again', shouldReport: true });
    }

    setIsLoading(false);
    return success;
  };

  const handleLocationEdit = async (editedLocation: Location) => {
    const success = await lazyLoadSelectedLocation(editedLocation);
    if (success) setIsEditingLocation(true);
  };

  const handleCancelLocationEdit = () => {
    setIsEditingLocation(false);
    setSelectedLocation(undefined);
  };

  const handleLocationUpdate: ApiSubmitHandler<LocationCreateDTO> = async (formLocation, { setFormErrors }) => {
    if (!selectedLocation) return;

    // TODO - pick out location patch fields until we can refactor LocationForm to be a generic with support for
    //        our create and update use-cases
    const { contacts: _selectedLocationContacts, ...locationUpdates } = formLocation;

    try {
      setIsSaving(true);
      setValidationErrors(undefined);

      const updatedLocation = await serviceTitanApi.locations.patch(selectedLocation.providerId, locationUpdates);
      onLocationsUpdated(locations.map((l) => (l.providerId === updatedLocation.providerId ? updatedLocation : l)));

      setIsSaving(false);

      const selectedLocationUpdated = { ...selectedLocation, ...updatedLocation };
      setSelectedLocation(selectedLocationUpdated);

      handleCancelLocationEdit();
    } catch (err) {
      setIsSaving(false);
      if (err instanceof BadRequestError) {
        setFormErrors(err);
        setValidationErrors(err.responseData?.nonFieldErrors);
      }
      reportException(err);
    }
  };

  const handleLocationAdd: ApiSubmitHandler<LocationCreateDTO> = async (formLocation, { setFormErrors }) => {
    const newLocation = { ...formLocation, providerCustomerId };

    try {
      setIsSaving(true);
      setValidationErrors(undefined);

      const { contacts, ...createdLocation } = await serviceTitanApi.locations.create(newLocation);

      onLocationContactsUpdated(createdLocation.providerId, contacts);
      onLocationsUpdated([...locations, createdLocation]);

      setIsSaving(false);

      handleCancelLocationEdit();
    } catch (err) {
      setIsSaving(false);

      if (err instanceof BadRequestError) {
        setFormErrors(err);
        setValidationErrors(err.responseData?.nonFieldErrors);
      }
      reportException(err);
    }
  };

  if (recoverableError) {
    return (
      <ManualErrorBoundary
        errorState={recoverableError}
        reportingCtx={{ extra: { companyId, providerCustomerId, locationContactsMap, locations } }}
        onRefresh={() => {
          setRecoverableError(null);
        }}
      />
    );
  }

  return (
    <>
      {/* Location Form */}
      {isEditingLocation && (
        <>
          <ControlHeader
            header={`${selectedLocation ? 'Edit' : 'Add'} Service Location`}
            controls={
              <>
                <IconButton type='submit' form='st-api-call-pop-location-form' key={'location-form-submit'}>
                  <SaveIcon />
                  {isSaving && <CircularProgress sx={{ position: 'absolute', zIndex: 1 }} />}
                </IconButton>
                <IconButton onClick={handleCancelLocationEdit}>
                  <CloseIcon />
                </IconButton>
              </>
            }
          />
          {validationErrors?.length && <ErrorDisplay errors={validationErrors} />}
          {selectedLocation && !isLoading ? (
            <LocationForm
              location={selectedLocation}
              onLocationSubmit={handleLocationUpdate}
              providerCustomerId={providerCustomerId}
              isDisabled={isSaving || isLoading}>
              <Contacts
                contactsResource={serviceTitanApi.locations.contacts(selectedLocation.providerId)}
                contacts={selectedLocation.contacts}
                onContactsUpdated={(contacts: Contact[]) => {
                  onLocationContactsUpdated(selectedLocation.providerId, contacts);
                  setSelectedLocation({ ...selectedLocation, contacts });
                }}
                disabledFromParent={isSaving || isLoading}
              />
            </LocationForm>
          ) : (
            <LocationForm
              providerCustomerId={providerCustomerId}
              onLocationSubmit={handleLocationAdd}
              isDisabled={isSaving}
            />
          )}
        </>
      )}
      {/* Location List */}
      {!isEditingLocation && (
        <>
          <BackdropSpinner open={isLoading} />
          <ControlHeader
            header='Service Locations'
            controls={
              <Tooltip title='Add' placement='top'>
                <IconButton onClick={() => setIsEditingLocation(true)}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <LocationList
            locationList={locations || []}
            onLocationEdit={handleLocationEdit}
            onLocationClicked={handleLocationEdit}
          />
        </>
      )}
    </>
  );
};
